import React, { useState, useEffect, useRef } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useUser } from '../contexts/UserContext';
import { clearAppIdentity } from '../utils/appIdentity';
import { hubSignOut } from '../utils/signOut';

/**
 * Full-screen gate shown while a migrated account's data is being moved onto its new hub sign-in.
 *
 * The move is fully automatic and entirely backend-driven: the UserMigration trigger writes the
 * person's ledger row on sign-in, a 1-minute reconciler flips it to READY_TO_REMAP, and a
 * DynamoDB-stream worker rewrites every sub-carrying row — committing User.cognitoID last. There is
 * NO endpoint the client can call to start the move or read its progress, so this page REFLECTS the
 * move; it does not drive it, and it deliberately shows no fabricated progress bar or ETA.
 *
 * `migrationPending` (a token-sub vs data-sub mismatch, derived in UserContext) flips false the
 * instant the worker commits User.cognitoID, so re-reading it is the only completion signal there is.
 * Until then the app already works on the legacy sub through appIdentity's compatibility path — which
 * is exactly why the timed-out branch offers a way in rather than trapping anyone.
 */

const POLL_INTERVAL_MS = 5000;
// The rewrite itself takes ~1s once the reconciler picks the row up (≤1 min). Past this the move is
// off the normal path: a manifest not yet built, a HOLD_MANUAL two-identity merge awaiting a human,
// or a FAILED row. None of those clear by waiting here, and the app is fully usable on the legacy sub
// throughout, so we stop blocking and let the person continue rather than strand them behind a page
// that will never advance.
const POLL_TIMEOUT_MS = 120000;

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '2rem',
};

const AccountMigrationGate = ({ children }) => {
  const { migrationPending, fetchUser } = useUser();
  const [timedOut, setTimedOut] = useState(false);
  const [continued, setContinued] = useState(false);
  const startedAt = useRef(null);

  useEffect(() => {
    if (!migrationPending) return undefined;
    if (startedAt.current === null) startedAt.current = Date.now();
    const timer = setInterval(async () => {
      if (Date.now() - startedAt.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return;
      }
      clearAppIdentity(); // drop the memoised decision so the next resolve re-reads both subs
      await fetchUser();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [migrationPending, fetchUser]);

  // Converged (the remap committed and the two subs now agree), never pending, or the person chose to
  // carry on past a stuck move — either way, into the app on the sub the data is keyed on.
  if (!migrationPending || continued) return children;

  const logOut = () => hubSignOut();

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Finishing your account move">
      {!timedOut ? (
        <>
          <Spinner animation="border" role="status" className="mb-4" />
          <h2>Setting up your account</h2>
          <p className="text-muted" style={{ maxWidth: 520 }}>
            You now sign in through FITTWorks, and your password came with you. We're moving your data
            across so it's filed under your new sign-in. Nothing is deleted and nothing is created —
            only the record of who it belongs to is updated. This usually takes under a minute; please
            keep this window open.
          </p>
          <Button variant="link" className="mt-3" onClick={logOut}>
            Log out
          </Button>
        </>
      ) : (
        <>
          <h2>Still finishing up</h2>
          <p className="text-muted" style={{ maxWidth: 520 }}>
            Your data is safe and your account works normally while the last step finishes in the
            background. You can carry on now. If it's still pending tomorrow, contact support.
          </p>
          <Button variant="primary" className="mt-2" onClick={() => setContinued(true)}>
            Continue to LeanFITT
          </Button>
          <Button variant="link" className="mt-2" onClick={logOut}>
            Log out
          </Button>
        </>
      )}
    </div>
  );
};

export default AccountMigrationGate;
